import type { SectionView } from "~/shared/section/SectionViewSwitcher";

import { useMediaQuery } from "./use-media-query";

// Tailwind default breakpoint minimums (px). The column maps below mirror the grid
// classes in `shared/section/design-tokens.ts` — KEEP IN SYNC if those change:
//   cardsGrid:  grid-cols-1 sm:2 lg:3 2xl:4
//   grid-tiles: grid-cols-3 sm:4 md:5 lg:6 xl:8 2xl:10
const SM = 640;
const MD = 768;
const LG = 1024;
const XL = 1280;
const XXL = 1536;

/** Live column count of the card grid at the current viewport width. */
function useCardColumns(): number {
    const sm = useMediaQuery(`(min-width: ${SM}px)`);
    const lg = useMediaQuery(`(min-width: ${LG}px)`);
    const xxl = useMediaQuery(`(min-width: ${XXL}px)`);
    if (xxl) return 4;
    if (lg) return 3;
    if (sm) return 2;
    return 1;
}

/** Live column count of the dense grid-tiles layout at the current viewport width. */
function useGridTileColumns(): number {
    const sm = useMediaQuery(`(min-width: ${SM}px)`);
    const md = useMediaQuery(`(min-width: ${MD}px)`);
    const lg = useMediaQuery(`(min-width: ${LG}px)`);
    const xl = useMediaQuery(`(min-width: ${XL}px)`);
    const xxl = useMediaQuery(`(min-width: ${XXL}px)`);
    if (xxl) return 10;
    if (xl) return 8;
    if (lg) return 6;
    if (md) return 5;
    if (sm) return 4;
    return 3;
}

/** Number of columns rendered for `view` at the current width (table is 1-per-row). */
export function useGridColumnCount(view: SectionView): number {
    const card = useCardColumns();
    const grid = useGridTileColumns();
    return view === "grid" ? grid : card;
}

/** Round `target` UP to the nearest multiple of `cols` (never below one full row). */
export function roundUpToMultiple(target: number, cols: number): number {
    if (cols <= 1) return Math.max(1, target);
    return Math.max(cols, Math.ceil(target / cols) * cols);
}

/** Safety clamp so a huge target × many columns never asks the backend for an absurd page. */
const MAX_PAGE_SIZE = 200;

/**
 * Effective fetch size for a section list: in card/grid views the user's target page size
 * is rounded up to a full row of the current responsive column count (so the last row is
 * never half-empty); the table view keeps the target verbatim (one row per item).
 */
export function useSectionPageSize(view: SectionView, target: number): number {
    const cols = useGridColumnCount(view);
    if (view === "table") return target;
    return Math.min(MAX_PAGE_SIZE, roundUpToMultiple(target, cols));
}
